'use client'

import type { CSSProperties, ReactNode } from 'react'

// The Martini mark as a neon sign, like the one in the launch video. It stutters on when the page
// opens and never quite settles: each tube hums on its own clock, and the olive and the "a" in Bar
// drop out now and then. Hovering makes it flicker one piece at a time: the olive, the glass, the
// stem and the foot, then each letter in turn. Unlit tubes stay visible behind
// the light, as real neon does. The geometry is the mark's
// centre line at the mark's own tilt. Reduced motion: lit and still.
const TUBE = {
  glass: 'M190.3 31.3 L45.3 165.3 L195.7 192.8 Z',
  stem: 'M195.7 192.8 L258 260.1',
  foot: 'M212 302.8 L304.1 217.6',
}
const OLIVE = { cx: 24.6, cy: 106.7, r: 19 }

const NEON_GLOW = '#ffc979'
const CORE = '#fff6e8'

const CSS = `
@keyframes neon-boot {
  0%, 6% { opacity: 0 } 7% { opacity: 1 } 9% { opacity: .1 } 12% { opacity: 1 } 14% { opacity: .25 }
  22% { opacity: .25 } 24% { opacity: 1 } 27% { opacity: .5 } 30%, 100% { opacity: 1 }
}
@keyframes neon-hum {
  0%, 100% { opacity: 1 } 18% { opacity: .9 } 19% { opacity: 1 } 47% { opacity: .94 } 48% { opacity: 1 }
  71% { opacity: .35 } 71.6% { opacity: 1 } 72.4% { opacity: .5 } 73% { opacity: 1 }
}
@keyframes neon-drop {
  0%, 86%, 100% { opacity: 1 } 87% { opacity: .15 } 88.5% { opacity: 1 } 90% { opacity: .2 } 93% { opacity: .2 } 94% { opacity: 1 }
}
@keyframes neon-zap {
  0% { opacity: 1 } 10% { opacity: .2 } 20% { opacity: 1 } 30% { opacity: .1 } 45% { opacity: 1 } 55% { opacity: .5 } 70%, 100% { opacity: 1 }
}
.neon-boot { animation: neon-boot 1.8s steps(1, end) both; }
.neon-hum { animation: neon-hum var(--hum, 6s) steps(1, end) var(--hum-delay, 2s) infinite; }
.neon-drop { animation: neon-drop var(--drop, 7s) steps(1, end) var(--drop-delay, 3s) infinite; }
.neon-sign:hover .neon-zap { animation: neon-zap .45s steps(1, end) var(--zap-delay, 0s) both; }
@media (prefers-reduced-motion: reduce) { .neon-boot, .neon-hum, .neon-drop, .neon-sign .neon-zap { animation: none !important; } }
`

function Tubes({ width, stroke }: { width: number; stroke: string }) {
  return (
    <g fill="none" stroke={stroke} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round">
      <path d={TUBE.glass} />
      <path d={TUBE.stem} />
      <path d={TUBE.foot} />
    </g>
  )
}

const glow = (k: number) =>
  `drop-shadow(0 0 ${2 * k}px ${NEON_GLOW}) drop-shadow(0 0 ${10 * k}px ${NEON_GLOW}) drop-shadow(0 0 ${30 * k}px ${NEON_GLOW})`
const wordGlow = (k: number) =>
  `0 0 ${4 * k}px ${NEON_GLOW}, 0 0 ${16 * k}px ${NEON_GLOW}, 0 0 ${42 * k}px ${NEON_GLOW}, 0 0 ${90 * k}px rgba(255,201,121,0.5)`

/** One light: boots once, then hums on its own clock. With `zap`, it also flickers on hover after
 * that many seconds; that layer sits innermost, so it never restarts the boot or the hum. */
function Lit({
  as = 'g',
  delay = 0,
  hum,
  drop,
  zap,
  children,
}: {
  as?: 'g' | 'span'
  delay?: number
  hum?: [number, number]
  drop?: [number, number]
  zap?: number
  children: ReactNode
}) {
  const Tag = as
  const inline = as === 'span' ? 'inline-block' : ''
  const clock = {
    '--hum': hum && `${hum[0]}s`,
    '--hum-delay': hum && `${hum[1]}s`,
    '--drop': drop && `${drop[0]}s`,
    '--drop-delay': drop && `${drop[1]}s`,
  } as CSSProperties
  return (
    <Tag className={`neon-boot ${inline}`} style={{ animationDelay: `${delay}s` }}>
      <Tag className={`${hum ? 'neon-hum' : drop ? 'neon-drop' : ''} ${inline}`} style={clock}>
        {zap === undefined ? (
          children
        ) : (
          <Tag className={`neon-zap ${inline}`} style={{ '--zap-delay': `${zap}s` } as CSSProperties}>
            {children}
          </Tag>
        )}
      </Tag>
    </Tag>
  )
}

// The hover flicker's order: the sign's parts, then the letters, one after another.
const WORD = 'Martini Bar'
const PART_STEP = 0.07
const LETTER_START = 0.3
const LETTER_STEP = 0.06

export function NeonSign() {
  const tube = (d: string) => (
    <path d={d} fill="none" stroke={CORE} strokeWidth={12} strokeLinecap="round" strokeLinejoin="round" />
  )
  return (
    <div role="img" aria-label="Martini Bar" className="neon-sign relative flex flex-col items-center px-6 py-2">
      <style>{CSS}</style>
      <svg viewBox="-16 10 338 312" className="h-[110px] w-auto overflow-visible md:h-[150px]" aria-hidden>
        {/* Unlit glass. Solid strokes faded as one group, so the joins where tubes meet don't stack
            into brighter spots. */}
        <g opacity={0.12}>
          <Tubes width={16} stroke="#fff" />
          <circle {...OLIVE} fill="none" stroke="#fff" strokeWidth={16} />
        </g>
        <g style={{ filter: glow(1) }}>
          <Lit delay={0.25} drop={[7.1, 3]} zap={0}>
            <circle {...OLIVE} fill="none" stroke={CORE} strokeWidth={12} />
          </Lit>
          <Lit hum={[6.3, 2]} zap={PART_STEP}>
            {tube(TUBE.glass)}
          </Lit>
          <Lit hum={[6.3, 2]} zap={PART_STEP * 2}>
            {tube(TUBE.stem)}
          </Lit>
          <Lit hum={[6.3, 2]} zap={PART_STEP * 3}>
            {tube(TUBE.foot)}
          </Lit>
        </g>
      </svg>
      <span
        aria-hidden
        className="mt-4 block whitespace-nowrap text-[48px] font-medium leading-none tracking-[-0.01em] sm:text-[60px] md:text-[76px]"
        style={{ color: CORE, textShadow: wordGlow(1) }}
      >
        <Lit as="span" delay={0.45} hum={[8.9, 4]}>
          {[...WORD].map((ch, i) => {
            const zap = LETTER_START + i * LETTER_STEP
            if (ch === ' ') return <span key={i}> </span>
            // The "a" in Bar also drops out on its own, as in the launch video.
            if (i === WORD.length - 2)
              return (
                <Lit as="span" key={i} drop={[9.4, 5]} zap={zap}>
                  {ch}
                </Lit>
              )
            return (
              <span key={i} className="neon-zap inline-block" style={{ '--zap-delay': `${zap}s` } as CSSProperties}>
                {ch}
              </span>
            )
          })}
        </Lit>
      </span>
    </div>
  )
}
